import { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { Calendar, Clock, User, ArrowRight, Share2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { getPostById, getRelatedPosts } from '../lib/blog';
import { BlogPostMetadata } from '../types/blog';
import { ReadingProgress } from '../components/blog/ReadingProgress';
import { MDXComponents } from '../components/blog/MDXComponents';
import { MDXProvider } from '@mdx-js/react';

export function InsightArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const [article, setArticle] = useState<{
    metadata: BlogPostMetadata;
    Component: React.ComponentType;
  } | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<BlogPostMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const articleContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadArticle() {
      if (!articleId) return;
      
      setLoading(true);
      const post = await getPostById(articleId);
      
      if (post) {
        console.log('Article loaded:', post);
        console.log('Component type:', typeof post.Component);
        console.log('Component:', post.Component);
        setArticle(post);
        setRelatedArticles(getRelatedPosts(articleId, 2));
      }
      
      setLoading(false);
    }

    loadArticle();
  }, [articleId]);

  const handleShare = async () => {
    if (navigator.share && article) {
      try {
        await navigator.share({
          title: article.metadata.title,
          text: article.metadata.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or error occurred
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#34AFFB] border-r-transparent"></div>
          <p className="mt-4 text-[#4B5563]">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F4F7F9] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl text-[#1f2937] mb-4">Article Not Found</h1>
          <Link to="/insights" className="text-[#34AFFB] hover:underline">
            Return to Insights
          </Link>
        </div>
      </div>
    );
  }

  const { metadata, Component } = article;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <ReadingProgress target={articleContentRef} />
      
      {/* Breadcrumbs */}
      <section className="py-6 bg-[#F4F7F9] border-b border-[#4B5563]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs 
            items={[
              { label: 'Home', href: '/' },
              { label: 'Insights', href: '/insights' },
              { label: metadata.title }
            ]}
          />
        </div>
      </section>

      {/* Article Header */}
      <article className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold inline-block mb-4">
                  {metadata.category}
                </span>
                <h1 className="text-4xl lg:text-5xl text-[#1f2937] mb-6">
                  {metadata.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-6 text-[#4B5563] mb-6">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    <div>
                      <div className="font-medium text-[#1f2937]">{metadata.author}</div>
                      <div className="text-sm">{metadata.authorRole}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {metadata.date}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    {metadata.readTime}
                  </div>
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 text-[#34AFFB] hover:text-[#1E88E5] transition-colors ml-auto"
                  >
                    <Share2 className="h-5 w-5" />
                    <span className="text-sm font-medium">Share</span>
                  </button>
                </div>

                {metadata.tags && metadata.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {metadata.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-[#F4F7F9] text-[#4B5563] rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Featured Image */}
              <div className="rounded-2xl overflow-hidden mb-12">
                <ImageWithFallback
                  src={metadata.imageUrl}
                  alt={metadata.title}
                  className="w-full h-96 object-cover"
                />
              </div>

              {/* Article Content */}
              <div 
                ref={articleContentRef}
                className="article-content"
                style={{
                  maxWidth: 'none',
                }}
              >
                <MDXProvider components={MDXComponents}>
                  <Component />
                </MDXProvider>
              </div>
            </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-[#F4F7F9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl text-[#1f2937] mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedArticles.map((related) => (
                <Link 
                  key={related.id} 
                  to={`/insights/${related.id}`}
                  className="group"
                >
                  <div className="bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-shadow">
                    <div className="relative h-48 overflow-hidden">
                      <ImageWithFallback
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[#34AFFB] text-white rounded-full text-sm">
                          {related.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-[#4B5563] mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {related.date}
                        </div>
                      </div>
                      <h3 className="text-xl text-[#1f2937] mb-3 group-hover:text-[#34AFFB] transition-colors">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-2 text-[#34AFFB] font-medium">
                        Read Article
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
